import type { FC, PropsWithChildren } from 'react';
import * as Sentry from '@sentry/react';
import { SplashScreen } from 'src/components/splash-screen';
import { useAsyncMountEffect } from 'src/hooks/use-async-mount-effect';
import { loadErrorCatalogs } from 'src/libs/error-catalog';

/**
 * Warm the localized error catalogs before the app renders.
 *
 * Error catalogs are fetched as static assets (see src/libs/error-catalog).
 * Reads are synchronous, so an error rendered before the catalog has loaded
 * would show the EN fallback template and never update. This provider gates the
 * tree on the initial load so children always read against a populated cache.
 *
 * Loading is best-effort: a failed catalog is logged and reported to Sentry,
 * then we proceed anyway — renderError falls back to the EN template shipped in
 * the ErrorBody itself.
 */
export const ErrorCatalogProvider: FC<PropsWithChildren> = ({ children }) => {
  const { isRunning } = useAsyncMountEffect(async () => {
    const results = await loadErrorCatalogs();

    // report missing locales to Sentry (silently)
    results.forEach((result) => {
      if (result.status === 'failure')
        Sentry.captureException(result.error, {
          extra: { locale: result.locale }
        });
    });
  });

  if (isRunning)
    return (
      <SplashScreen
        layer="error-catalog"
        variant="loading"
      />
    );

  return <>{children}</>;
};
